import {FC, useCallback, MouseEvent, useRef, useState, useEffect} from 'react';
import {usePlayerContext} from '../../hooks/usePlayerContext';
import {useIsSeeking, useUiState} from '../../hooks/usePlayerState';
import {useSeek} from '../../hooks/useSeek';
import {mouseIsOverElement} from '../../utils/mouseIsOverElement';
import {formatTimestampByDuration} from '../../utils/renderTimestamp';
import {secondsToText} from '../../utils/secondsToText';
import {Tooltip} from '../tooltip';
import {calculateBuffer} from './calculateBuffer';
import {getOffsetFromTarget} from './getOffsetFromTarget';
import {getPercentageFromMouseEvent} from './getPercentageFromMouseEvent';
import {getValueFromPercentage} from './getValueFromPercentage';
import {Handle} from './handle';
import * as Styles from './progressBar.styles';

function calculateProgress(time: number, duration: number): number {
  // If duration is `0`, we can't divide by it. Just assume we've played none.
  if (duration === 0) {
    return 0;
  }

  return (time / duration) * 100;
}

function getHandlePosition(
  event: MouseEvent<HTMLElement> | globalThis.MouseEvent,
  container: HTMLDivElement
): number {
  const percentage = getPercentageFromMouseEvent(event, container);
  return (percentage / 100) * container.clientWidth;
}

type HoverState = {
  isHovering: boolean;
  hoveredPosition: number | null;
  hoveredTime: number | null;
};

export const ProgressBar: FC = () => {
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const {accentColor, player} = usePlayerContext();
  const isSeeking = useIsSeeking();
  const [state] = useUiState();
  const onSeek = useSeek();
  const {duration, isLive, timestamp, buffered} = state.context;
  const [hoverState, setHoverState] = useState<HoverState>({
    isHovering: false,
    hoveredPosition: null,
    hoveredTime: null,
  });
  const [isDragging, setIsDragging] = useState(false);
  const [handlePosition, setHandlePosition] = useState(0);

  // We want to have the UI reflect the seeking status as soon as the user releases the mouse,
  // without any visual glitches between the time the user releases the mouse and the player state
  // machine updates, so we manage the ProgressBar's UI state seperately
  const [uiIsSeeking, setUiIsSeeking] = useState(isSeeking);

  useEffect(() => {
    // If seeking has stopped, update the UI seek status accordingly
    if (!isSeeking) {
      setUiIsSeeking(false);
    }
  }, [isSeeking]);

  const drag = useCallback(
    (event: globalThis.MouseEvent): void => {
      const $container = containerRef.current;

      if (!$container) return;

      // Calculate the hovered percentage as a timestamp
      setHandlePosition(getHandlePosition(event, $container));
    },
    [containerRef]
  );

  const seekPlayer = useCallback(
    (percentage: number) => {
      if (!player) return;

      if (percentage <= 0) {
        onSeek(0);
      } else if (percentage >= 100) {
        onSeek(duration);
      } else {
        const timestamp = getValueFromPercentage(percentage, duration);
        onSeek(timestamp);
      }
    },
    [player, onSeek, duration]
  );

  const handleMouseEvent = useCallback(
    (event: MouseEvent<HTMLDivElement> | globalThis.MouseEvent): void => {
      const handleMouseUp = (event: globalThis.MouseEvent): void => {
        setIsDragging(false);

        window.removeEventListener('mousemove', handleMouseEvent);
        window.removeEventListener('mousemove', drag);
        window.removeEventListener('mouseup', handleMouseUp);

        // Seek the player to the timestamp where the mouse up event occured
        const $container = containerRef.current;
        const $wrapper = wrapperRef.current;
        if (!$container || !$wrapper || !player) return;

        const mouseIsOver = mouseIsOverElement(
          event,
          $wrapper.getBoundingClientRect()
        );

        setHoverState((prevState) => ({
          isHovering: mouseIsOver,
          hoveredPosition: prevState.hoveredPosition,
          // Set hoveredPosition and hoveredTime to null so the tooltip disapears on seekbar click
          // We want the tooltip to disappear but the handle to remain after click.
          hoveredTime: null,
        }));

        const percentage = getPercentageFromMouseEvent(event, $container);
        seekPlayer(percentage);
      };

      const $container = containerRef.current;

      if (!$container) return;

      const {type} = event;

      // Calculate the hovered percentage as a timestamp
      const percentage = getPercentageFromMouseEvent(event, $container);

      switch (type) {
        case 'mousemove':
        case 'mouseenter': {
          setHoverState({
            isHovering: true,
            hoveredTime: getValueFromPercentage(percentage, duration),
            hoveredPosition: getOffsetFromTarget(event, $container),
          });

          return;
        }
        case 'mouseleave': {
          setHoverState((prevState) => ({
            isHovering: isDragging,
            hoveredTime: prevState.hoveredTime,
            hoveredPosition: prevState.hoveredPosition,
          }));
          return;
        }
        case 'mousedown': {
          setIsDragging(true);
          setUiIsSeeking(true);
          // Update the handlePosition to be the location of the cursor
          setHandlePosition(getHandlePosition(event, $container));

          window.addEventListener('mousemove', drag);
          window.addEventListener('mousemove', handleMouseEvent);
          window.addEventListener('mouseup', handleMouseUp);
          return;
        }
        default:
          return;
      }
    },
    [containerRef, duration, isDragging, drag, player, seekPlayer]
  );

  const handleClick = useCallback(
    (event: MouseEvent<HTMLDivElement>): void => {
      const $container = containerRef.current;

      if (!$container || !player) return;

      const percentage = getPercentageFromMouseEvent(event, $container);

      // Calculate the hovered percentage as a timestamp
      setHandlePosition((percentage / 100) * $container.clientWidth);
      seekPlayer(percentage);
    },
    [seekPlayer, player]
  );

  // Don't render a progressbar on live videos.
  if (isLive) return null;

  const progress = calculateProgress(timestamp, duration);
  const buffer = calculateBuffer(buffered, timestamp, duration);

  const calculateHandlePosition = (): number => {
    if (isDragging || uiIsSeeking) return handlePosition;

    const $container = containerRef.current;

    if (!$container) return handlePosition;

    return (progress / 100) * $container.clientWidth;
  };

  const ariaMinMax = [0, duration];
  const ariaValueText = `${secondsToText(timestamp)} out of ${secondsToText(
    duration
  )}`;

  return (
    <Styles.Wrapper
      onMouseEnter={handleMouseEvent}
      onMouseLeave={handleMouseEvent}
      onMouseMove={handleMouseEvent}
      onMouseOver={handleMouseEvent}
      onMouseDown={handleMouseEvent}
      onClick={handleClick}
      ref={wrapperRef}
    >
      <Styles.Container
        role="slider"
        ref={containerRef}
        aria-label="Video Timeline"
        aria-valuemin={ariaMinMax[0]}
        aria-valuemax={ariaMinMax[1]}
        aria-valuenow={Math.floor(timestamp || 0)}
        aria-valuetext={ariaValueText}
        tabIndex={0}
      >
        <Styles.Progress
          progress={buffer}
          color="rgba(255, 255, 255, 0.2)"
          data-testid="buffer"
        />
        <Styles.Progress
          progress={progress}
          color={accentColor}
          data-testid="progress"
        />
        <Handle
          position={`${calculateHandlePosition()}px`}
          visible={hoverState.isHovering}
        />
        <Tooltip
          position={`${hoverState.hoveredPosition}px`}
          label={
            hoverState.hoveredTime !== null
              ? formatTimestampByDuration(hoverState.hoveredTime, duration)
              : ''
          }
          visible={hoverState.hoveredTime !== null && hoverState.isHovering}
        />
      </Styles.Container>
    </Styles.Wrapper>
  );
};
